import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Card , Spinner } from "react-bootstrap";
import { IoMdRemoveCircle, IoMdCall } from "react-icons/io";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { updatePlace, getPlace } from "../../store/slices/place";
import { uploadImage } from "../../utils/helpers/fileupload";
import { useParams } from "react-router-dom";
import { FaImage } from "react-icons/fa";

export default function EditPlace() {
  const { id } = useParams();
  const user = JSON.parse(localStorage.getItem("user"));
  const token = user?.data?.accessToken;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const initialForm = {
    name: "",
  };
  const [form, setForm] = useState(initialForm);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPlace = async () => {
      try {
        let item = { id };
        const res = await dispatch(getPlace(item)).unwrap();

        const place = res?.places[0];

        if (place) {
          setForm({ name: place.name || "" });
          setPreview(place.image || null);
        }
      } catch (err) {
        toast.error("Failed to fetch place details");
      }
    };

    if (id) fetchPlace();
  }, [dispatch, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => {
      const newErrors = { ...prev };

      switch (name) {
        case "name":
          const nameRegex = /^[a-zA-Z0-9][\s\S]*$/;

          if (!value.trim()) {
            newErrors.name = "Please enter place name";
          } else if (!nameRegex.test(value.trim())) {
            newErrors.name = "Please enter letters first";
          } else {
            delete newErrors.name;
          }
          break;

        default:
          break;
      }

      return newErrors;
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;

    // file size limits

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        image: "Image size must be less than 5MB",
      }));

      return;
    }

    setImage(file);
    setPreview(URL.createObjectURL(file));

    setErrors((prev) => {
      const copy = { ...prev };
      delete copy.image;
      return copy;
    });
  };

  const removeImage = () => {
    setImage(null);
    setPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};

    if (!form.name.trim()) newErrors.name = "Please enter place name";

    if (!image && !preview) {
      newErrors.image = "Please upload place image";
    } else if (image && image.size > 5 * 1024 * 1024) {
      newErrors.image = "Image size must be less than 5MB";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);

      let fileUrl = image ? " " : preview;

      if (image) {
        fileUrl = await uploadImage(image, token);
      }

      const payload = {
        id,
        name: form.name.trim(),
        image: image ? fileUrl : preview,
      };

      await dispatch(updatePlace(payload)).unwrap();

      toast.success("Place edited successfully");

      setForm(initialForm);
      setImage(null);
      setPreview(null);
      setErrors({});

      navigate("/admin/places");
    } catch (err) {
      toast.error(err || "Failed to edit place");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/admin/home" className="breadcrumb-link active">
            Home
          </Link>

          <span className="breadcrumb-separator">|</span>

          <Link to="/admin/places" className="breadcrumb-link active">
            Places
          </Link>

          <span className="breadcrumb-separator">|</span>

          <span className="breadcrumb-link inactive">Edit Place</span>
        </div>
      </div>

      <Container fluid className="p-4">
        <Card style={{ border: "none" }}>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Row className="mb-4">
                <Col md={4}>
                  <Form.Label>Place Name</Form.Label>
                  <Form.Control
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter place name"
                    minLength={3}
                    maxLength={30}
                  />

                  {errors.name && (
                    <div className="text-danger small mt-1">{errors.name}</div>
                  )}
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Image</Form.Label>

                    <div
                      className="custom-file-uploader"
                      onClick={() =>
                        document.getElementById("imageUpload").click()
                      }
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        handleImageChange({ target: { files: [file] } });
                      }}
                    >
                      {!preview ? (
                        <div className="upload-placeholder">
                          <div className="upload-icon-box">
                            <FaImage className="upload-icon-white" />
                          </div>
                          <p className="mb-1">Click or Drag Image Here</p>
                        </div>
                      ) : (
                        <div className="image-preview-wrapper">
                          <img
                            src={preview}
                            alt="preview"
                            className="preview-img"
                          />
                          <IoMdRemoveCircle
                            size={24}
                            className="remove-icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage();
                            }}
                          />
                        </div>
                      )}

                      <Form.Control
                        id="imageUpload"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        hidden
                      />
                    </div>

                    {errors.image && (
                      <small className="text-danger d-block mt-2">
                        {errors.image}
                      </small>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        variant="primary"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}
